import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { fetchAdmins } from '../AdminManagement/adminApi';
import type { AdminRecord } from '../AdminManagement/adminApi';
import {
  fetchRegistry, fetchKeysForAdmin, fetchConfig, saveConfig,
  createKey, updateKey, deleteKey, resetKey, testKey,
  pingKeylessProvider,
} from './aiAdminApi';
import type {
  AiKey, AiConfig, KeyStatus, KeylessProviderHealth, Registry, Provider, FeatureKey, KeyPayload,
} from './aiAdminApi';
import './AiAdminPage.scss';

const PROVIDER_LABEL: Record<Provider, string> = {
  mistral_paid: 'Mistral Paid',
  mistral_free: 'Mistral Free',
  custom_ai: 'AI API tự xây',
  gemini: 'Google Gemini API',
  ollama_local: 'Ollama local (self-hosted)',
  paddleocr_local: 'PaddleOCR-VL Local',
};

const PROVIDER_DESC: Record<Provider, string> = {
  mistral_paid: 'API Mistral OCR có phí — 1 key duy nhất cho đơn vị này.',
  mistral_free: 'Mistral free tier — dán nhiều key, tự rotate khi rate-limit.',
  custom_ai: 'RoutesAI / Gemini tự host — điền URL base, token optional.',
  gemini: 'Google Gemini API trực tiếp (aistudio.google.com/app/apikey). Hỗ trợ OCR CCCD/GPLX, gửi 1-2 ảnh/giấy tờ.',
  ollama_local: 'Ollama chạy trên máy local (mặc định http://127.0.0.1:11434). Cần "model" name (vd qwen2.5vl:7b, paddleocr-vl). Không cần secret.',
  paddleocr_local: 'PaddleOCR-VL 0.9B chạy 100% local trên GPU của máy chủ. Mặc định luôn sẵn sàng — không cần API key, không cần cấu hình endpoint. Tốc độ ~10-30s/ảnh, miễn phí.',
};

const EMPTY_PAYLOAD = (provider: Provider): KeyPayload => ({
  provider,
  label: '',
  secret: '',
  endpointUrl:
    provider === 'custom_ai'
      ? 'http://127.0.0.1:3019'
      : provider === 'ollama_local'
        ? 'http://127.0.0.1:11434'
        : '',
  model: provider === 'ollama_local' ? 'qwen2.5vl:7b' : '',
});

// ─── Key create/edit modal ────────────────────────────────────────────────

type ModalProps = {
  adminId: number;
  editing: AiKey | null;
  defaultProvider: Provider;
  registry: Registry;
  onClose: () => void;
  onSaved: () => void;
};

const KeyModal: React.FC<ModalProps> = ({ adminId, editing, defaultProvider, registry, onClose, onSaved }) => {
  const [form, setForm] = useState<KeyPayload>(() =>
    editing
      ? {
          provider: editing.provider,
          label: editing.label,
          secret: '',
          endpointUrl: editing.endpointUrl ?? '',
          model: editing.model ?? '',
        }
      : EMPTY_PAYLOAD(defaultProvider),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isEdit = !!editing;
  const isCustom = form.provider === 'custom_ai';
  const isGemini = form.provider === 'gemini';
  const geminiOpts = registry.providerOptions?.gemini;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.label.trim()) { setErr('Label không được để trống'); return; }
    if (!isEdit) {
      if (isCustom && !form.endpointUrl?.trim()) { setErr('Custom AI cần endpointUrl'); return; }
      if (!isCustom && !form.secret?.trim()) { setErr('Cần dán key API'); return; }
    }
    setSaving(true);
    setErr(null);
    try {
      if (isEdit) {
        await updateKey(editing!.id, {
          label: form.label,
          secret: form.secret || undefined,
          endpointUrl: form.endpointUrl || undefined,
          model: form.model || undefined,
        });
        toast.success('Đã cập nhật key');
      } else {
        await createKey(adminId, form);
        toast.success('Đã thêm key');
      }
      onSaved();
      onClose();
    } catch (e) {
      const msg = (e as { response?: { data?: { EM?: string } } })?.response?.data?.EM
        || (e instanceof Error ? e.message : 'Lỗi');
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="aim__overlay" onClick={onClose}>
      <div className="aim__modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="aim__modal-title">
          {isEdit ? `Sửa key · ${PROVIDER_LABEL[form.provider]}` : `Thêm key · ${PROVIDER_LABEL[form.provider]}`}
        </h3>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="aim__field">
            <label className="aim__label">Tên gợi nhớ</label>
            <input
              className="aim__input"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="VD: Key Prod chính / Free #1"
              autoFocus
            />
          </div>

          {isCustom ? (
            <>
              <div className="aim__field">
                <label className="aim__label">Endpoint URL</label>
                <input
                  className="aim__input"
                  value={form.endpointUrl ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, endpointUrl: e.target.value }))}
                  placeholder="http://127.0.0.1:3019"
                />
              </div>
              <div className="aim__field">
                <label className="aim__label">Bearer token (optional)</label>
                <input
                  className="aim__input"
                  value={form.secret ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
                  placeholder={isEdit ? 'Để trống = giữ token cũ' : 'Bỏ trống nếu API không cần'}
                />
              </div>
            </>
          ) : (
            <div className="aim__field">
              <label className="aim__label">API Key {isEdit && <span className="aim__help">(để trống = giữ key cũ)</span>}</label>
              <textarea
                className="aim__textarea"
                value={form.secret ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
                placeholder={isEdit ? '' : 'sk-...'}
              />
            </div>
          )}

          {isGemini && geminiOpts?.models && (
            <div className="aim__field">
              <label className="aim__label">Model Gemini</label>
              <select
                className="aim__select"
                value={form.model ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
              >
                <option value="">Mặc định ({geminiOpts.defaultModel || 'gemini-2.5-flash'})</option>
                {geminiOpts.models.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <span className="aim__help">
                Để trống dùng model mặc định. Mỗi key có thể chọn model riêng — orchestrator
                fallback giữa chúng theo thứ tự ma trận.
              </span>
            </div>
          )}

          {err && <p className="aim__error">{err}</p>}
          <div className="aim__modal-footer">
            <button type="button" className="aim__btn aim__btn--ghost" onClick={onClose} disabled={saving}>Hủy</button>
            <button type="submit" className="aim__btn aim__btn--primary" disabled={saving}>
              {saving ? 'Đang lưu…' : isEdit ? 'Cập nhật' : 'Thêm'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

// ─── Keys panel for a given admin ─────────────────────────────────────────

type KeysPanelProps = { adminId: number; registry: Registry };

const KeysPanel: React.FC<KeysPanelProps> = ({ adminId, registry }) => {
  const [keys, setKeys] = useState<AiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ provider: Provider; editing: AiKey | null } | null>(null);
  // Health snapshots per keyless provider (eg paddleocr_local). Refreshed
  // on mount + every 30s while the panel is visible.
  const [keylessHealth, setKeylessHealth] = useState<Partial<Record<Provider, KeylessProviderHealth>>>({});

  const keylessProviders = useMemo<Provider[]>(
    () => (registry.keylessProviders ?? []) as Provider[],
    [registry.keylessProviders],
  );

  const refreshKeylessHealth = useCallback(async () => {
    if (keylessProviders.length === 0) return;
    const results = await Promise.all(
      keylessProviders.map(async (p) => {
        try {
          const r = await pingKeylessProvider(p);
          return [p, r.EC === 0 ? r.DT : { ok: false, error: r.EM }] as const;
        } catch (e) {
          return [p, { ok: false, error: (e as Error).message }] as const;
        }
      }),
    );
    setKeylessHealth(Object.fromEntries(results));
  }, [keylessProviders]);

  useEffect(() => {
    refreshKeylessHealth();
    const id = window.setInterval(refreshKeylessHealth, 30000);
    return () => window.clearInterval(id);
  }, [refreshKeylessHealth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchKeysForAdmin(adminId);
      if (r.EC === 0) setKeys(r.DT);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => { load(); }, [load]);

  const byProvider = useMemo(() => {
    const map: Record<Provider, AiKey[]> = { mistral_paid: [], mistral_free: [], custom_ai: [], gemini: [], ollama_local: [], paddleocr_local: [] };
    for (const k of keys) map[k.provider]?.push(k);
    return map;
  }, [keys]);

  const handleDelete = async (k: AiKey) => {
    if (!window.confirm(`Xoá key "${k.label}"?`)) return;
    try {
      await deleteKey(k.id);
      toast.success('Đã xoá');
      load();
    } catch {
      toast.error('Xoá thất bại');
    }
  };

  const handleReset = async (k: AiKey) => {
    try {
      await resetKey(k.id);
      toast.success('Đã reset');
      load();
    } catch {
      toast.error('Reset thất bại');
    }
  };

  const handleTest = async (k: AiKey) => {
    const tid = toast.loading('Đang test…');
    try {
      const r = await testKey(k.id);
      if (r.EC === 0 && r.DT.ok) {
        toast.update(tid, { render: `OK · ${r.DT.latencyMs}ms`, type: 'success', isLoading: false, autoClose: 2500 });
      } else {
        const err = r.DT.ok ? '' : r.DT.error;
        toast.update(tid, { render: `Fail · ${err}`, type: 'error', isLoading: false, autoClose: 4000 });
      }
      load();
    } catch {
      toast.update(tid, { render: 'Lỗi', type: 'error', isLoading: false, autoClose: 3000 });
    }
  };

  if (loading && !keys.length) {
    return <div className="aim__empty">Đang tải…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {registry.providers.map((provider) => {
        const keyless = keylessProviders.includes(provider);
        if (keyless) {
          // Read-only health card. No "+ Add key", no per-row CRUD —
          // service is fixed at the env-configured endpoint.
          const health = keylessHealth[provider];
          const endpoint = registry.providerOptions?.[provider]?.endpoint || '';
          const statusClass = !health
            ? 'aim__keyless-status--probing'
            : health.ok && health.warm
              ? 'aim__keyless-status--ok'
              : health.ok
                ? 'aim__keyless-status--cold'
                : 'aim__keyless-status--down';
          const statusText = !health
            ? 'Đang kiểm tra…'
            : health.ok && health.warm
              ? 'sẵn sàng'
              : health.ok
                ? 'đang khởi động'
                : 'không kết nối được';
          return (
            <div key={provider} className="aim__provider-group aim__provider-group--keyless">
              <div className="aim__provider-header">
                <div>
                  <div className="aim__provider-name">
                    {PROVIDER_LABEL[provider]}
                    <span className={`aim__keyless-status ${statusClass}`}>● {statusText}</span>
                    <span className="aim__provider-desc">{PROVIDER_DESC[provider]}</span>
                  </div>
                </div>
                <button
                  className="aim__btn aim__btn--ghost aim__btn--sm"
                  onClick={refreshKeylessHealth}
                  title="Kiểm tra lại trạng thái service"
                >
                  ↻ Refresh
                </button>
              </div>
              <div className="aim__keyless-body">
                <div className="aim__keyless-row">
                  <span className="aim__keyless-label">Endpoint:</span>
                  <code>{endpoint || '(env mặc định)'}</code>
                </div>
                {health?.model && (
                  <div className="aim__keyless-row">
                    <span className="aim__keyless-label">Model:</span>
                    <code>{health.model}</code>
                  </div>
                )}
                {health?.loadedAt && (
                  <div className="aim__keyless-row">
                    <span className="aim__keyless-label">Loaded:</span>
                    <span>{new Date(health.loadedAt * 1000).toLocaleString('vi-VN')}</span>
                  </div>
                )}
                {health && !health.ok && (
                  <div className="aim__keyless-row aim__keyless-row--error">
                    <span className="aim__keyless-label">Lỗi:</span>
                    <span>{health.error}</span>
                  </div>
                )}
                <div className="aim__keyless-row aim__keyless-row--hint">
                  Provider này luôn sẵn sàng — không cần thêm key. Bật/tắt cho từng tính năng ở tab "Cấu hình tính năng".
                </div>
              </div>
            </div>
          );
        }
        return (
        <div key={provider} className="aim__provider-group">
          <div className="aim__provider-header">
            <div>
              <div className="aim__provider-name">
                {PROVIDER_LABEL[provider]}
                <span className="aim__provider-desc">{PROVIDER_DESC[provider]}</span>
              </div>
            </div>
            <button
              className="aim__btn aim__btn--primary aim__btn--sm"
              onClick={() => setModal({ provider, editing: null })}
            >
              + Thêm key
            </button>
          </div>

          {byProvider[provider].length === 0 ? (
            <div className="aim__empty">Chưa có key nào</div>
          ) : (
            byProvider[provider].map((k) => (
              <div key={k.id} className="aim__key-row">
                <div>
                  <div className="aim__key-label">{k.label}</div>
                  {k.lastErrorMessage && (
                    <div className="aim__key-meta" style={{ color: '#b91c1c' }}>
                      {k.lastErrorMessage}
                    </div>
                  )}
                </div>
                <div className="aim__key-secret">
                  {provider === 'custom_ai' ? (k.endpointUrl || '—') : k.secretMasked}
                  {provider === 'gemini' && k.model && (
                    <div style={{ fontSize: 11, color: '#3730a3', marginTop: 2 }}>
                      model: <b>{k.model}</b>
                    </div>
                  )}
                </div>
                <div>
                  <span className={`aim__status aim__status--${k.status}`}>{k.status}</span>
                </div>
                <div className="aim__key-meta">
                  {k.lastUsedAt ? `Dùng: ${new Date(k.lastUsedAt).toLocaleString('vi-VN')}` : 'Chưa dùng'}
                  {k.failCount > 0 && ` · fail×${k.failCount}`}
                </div>
                <div className="aim__key-actions">
                  <button className="aim__btn aim__btn--ghost aim__btn--sm" onClick={() => handleTest(k)}>Test</button>
                  {k.status !== 'active' && (
                    <button className="aim__btn aim__btn--ghost aim__btn--sm" onClick={() => handleReset(k)}>Reset</button>
                  )}
                  <button
                    className="aim__btn aim__btn--ghost aim__btn--sm"
                    onClick={() => setModal({ provider, editing: k })}
                  >
                    Sửa
                  </button>
                  <button className="aim__btn aim__btn--danger aim__btn--sm" onClick={() => handleDelete(k)}>Xoá</button>
                </div>
              </div>
            ))
          )}
        </div>
        );
      })}

      {modal && (
        <KeyModal
          adminId={adminId}
          editing={modal.editing}
          defaultProvider={modal.provider}
          registry={registry}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

// ─── Matrix panel (feature × engine) ──────────────────────────────────────

type MatrixPanelProps = { adminId: number; registry: Registry };

const STATUS_LABEL: Record<KeyStatus, string> = {
  active: 'sẵn sàng',
  rate_limited: 'đang nghỉ',
  expired: 'hết hạn',
  disabled: 'tắt',
};

const MatrixPanel: React.FC<MatrixPanelProps> = ({ adminId, registry }) => {
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [keys, setKeys] = useState<AiKey[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load config + keys together so we can show per-engine key counts
      // alongside the priority list (eg "Gemini · 3 keys (2 ready)").
      const [cfgRes, keysRes] = await Promise.all([
        fetchConfig(adminId),
        fetchKeysForAdmin(adminId),
      ]);
      if (cancelled) return;
      if (cfgRes.EC === 0) {
        setConfig(cfgRes.DT.aiConfig);
        setDirty(false);
      }
      if (keysRes.EC === 0) {
        setKeys(keysRes.DT || []);
      }
    })();
    return () => { cancelled = true; };
  }, [adminId]);

  /** Group keys by provider for the priority-list metadata. Active count
   *  excludes rate_limited / expired / disabled — that's the count the
   *  orchestrator can actually try right now. */
  const keysByProvider = useMemo(() => {
    const map: Record<Provider, AiKey[]> = {
      mistral_paid: [], mistral_free: [], custom_ai: [], gemini: [], ollama_local: [], paddleocr_local: [],
    };
    for (const k of keys) {
      if (map[k.provider]) map[k.provider].push(k);
    }
    return map;
  }, [keys]);

  /** Move engine at index `from` to `to`. Used by the up/down arrows.
   *  Bounds-checked so the keyboard shortcut at the list edges is a no-op. */
  const reorderEngine = (feature: FeatureKey, from: number, to: number) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const engines = [...(prev[feature]?.engines || [])];
      if (to < 0 || to >= engines.length || from < 0 || from >= engines.length) return prev;
      const [moved] = engines.splice(from, 1);
      engines.splice(to, 0, moved);
      return { ...prev, [feature]: { ...prev[feature], engines } };
    });
    setDirty(true);
  };

  const removeEngine = (feature: FeatureKey, engine: Provider) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const engines = (prev[feature]?.engines || []).filter((e) => e !== engine);
      // Parallel is meaningless with <2 engines — auto-uncheck so persisted
      // state stays consistent with what the UI shows.
      const parallel = engines.length >= 2 ? !!prev[feature]?.parallel : false;
      return { ...prev, [feature]: { engines, parallel } };
    });
    setDirty(true);
  };

  const addEngine = (feature: FeatureKey, engine: Provider) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const current = prev[feature]?.engines || [];
      if (current.includes(engine)) return prev;
      return { ...prev, [feature]: { ...prev[feature], engines: [...current, engine] } };
    });
    setDirty(true);
  };

  const toggleParallel = (feature: FeatureKey) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const engines = prev[feature]?.engines || [];
      if (engines.length < 2) return prev;
      return { ...prev, [feature]: { ...prev[feature], parallel: !prev[feature]?.parallel } };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await saveConfig(adminId, config);
      toast.success('Đã lưu cấu hình');
      setDirty(false);
    } catch {
      toast.error('Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <div className="aim__empty">Đang tải…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p className="aim__help">
        Mỗi tính năng có 1 chuỗi fallback. Engine <b>#1</b> được thử trước; trong engine
        đó, các <b>key/model</b> đã add cũng được rotate (vd Gemini có 3 key với model
        khác nhau → thử lần lượt). Khi <b>TẤT CẢ key</b> của #1 hết / lỗi 503 / 429 /
        timeout, orchestrator tự chuyển sang <b>#2</b>, rồi <b>#3</b>…
        Bấm <b>↑ ↓</b> để đổi thứ tự ưu tiên engine, <b>✕</b> để loại khỏi chuỗi.
        <b> Chạy song song</b> chỉ bật khi có ≥ 2 engine — gọi tất cả cùng lúc, lấy kết
        quả nhanh nhất (đắt hơn nhưng an toàn nhất).
      </p>

      <div className="aim__feature-list">
        {registry.features.map((f) => {
          const featureConfig = config[f.key] || { engines: [], parallel: false };
          const engines = featureConfig.engines || [];
          const parallel = !!featureConfig.parallel;
          const supportedProviders = registry.providers.filter((p) => f.supports.includes(p));
          const availableToAdd = supportedProviders.filter((p) => !engines.includes(p));

          return (
            <div key={f.key} className="aim__feature-card">
              <div className="aim__feature-header">
                <h3 className="aim__feature-title">{f.label}</h3>
                <span className="aim__feature-meta">
                  {engines.length === 0
                    ? 'Chưa cấu hình — feature sẽ trả lỗi khi gọi'
                    : engines.length === 1
                      ? '1 engine, không có fallback'
                      : `${engines.length} engines, fallback theo thứ tự`}
                </span>
              </div>

              {engines.length === 0 ? (
                <div className="aim__feature-empty">
                  <i className="material-icons">warning</i>
                  Chưa có engine nào. Bấm <b>+ Thêm engine</b> bên dưới.
                </div>
              ) : (
                <ol className="aim__priority-list">
                  {engines.map((engine, idx) => {
                    const isKeylessEngine = (registry.keylessProviders ?? []).includes(engine);
                    const engineKeys = keysByProvider[engine] || [];
                    const activeKeys = engineKeys.filter((k) => k.status === 'active');
                    // Keyless providers don't have key rows; the "no keys"
                    // warning doesn't apply to them — service is always
                    // available at the fixed endpoint.
                    const noKeys = !isKeylessEngine && engineKeys.length === 0;
                    return (
                      <li
                        key={engine}
                        className={`aim__priority-item${noKeys ? ' aim__priority-item--warning' : ''}`}
                      >
                        <span className="aim__priority-num">#{idx + 1}</span>
                        <span className="aim__priority-body">
                          <span className="aim__priority-name">{PROVIDER_LABEL[engine]}</span>
                          {isKeylessEngine ? (
                            <span className="aim__priority-keys">
                              ✓ Luôn sẵn sàng — service local, không cần key.
                            </span>
                          ) : noKeys ? (
                            <span className="aim__priority-keys aim__priority-keys--missing">
                              <i className="material-icons">error_outline</i>
                              Chưa có key — engine này sẽ luôn fail. Sang tab "Quản lý Keys" để thêm.
                            </span>
                          ) : (
                            <span className="aim__priority-keys">
                              {engineKeys.length} key · {activeKeys.length} sẵn sàng
                              {engineKeys.length > 1 && (
                                <span className="aim__priority-keys-models">
                                  {' '}({engineKeys.map((k) => k.model || k.label).join(' → ')})
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                        <span className="aim__priority-actions">
                          <button
                            type="button"
                            className="aim__icon-btn"
                            disabled={idx === 0}
                            onClick={() => reorderEngine(f.key, idx, idx - 1)}
                            title="Tăng độ ưu tiên"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="aim__icon-btn"
                            disabled={idx === engines.length - 1}
                            onClick={() => reorderEngine(f.key, idx, idx + 1)}
                            title="Giảm độ ưu tiên"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="aim__icon-btn aim__icon-btn--danger"
                            onClick={() => removeEngine(f.key, engine)}
                            title="Loại khỏi chuỗi fallback"
                          >
                            ✕
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}

              <div className="aim__feature-controls">
                {availableToAdd.length > 0 && (
                  <select
                    className="aim__add-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) addEngine(f.key, e.target.value as Provider);
                    }}
                  >
                    <option value="">+ Thêm engine vào chuỗi…</option>
                    {availableToAdd.map((p) => (
                      <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>
                    ))}
                  </select>
                )}

                <label
                  className={`aim__parallel-toggle ${engines.length < 2 ? 'aim__parallel-toggle--disabled' : ''}`}
                  title={
                    engines.length < 2
                      ? 'Cần ≥ 2 engine để bật chế độ song song'
                      : 'Gọi tất cả cùng lúc, lấy kết quả về sớm nhất'
                  }
                >
                  <input
                    type="checkbox"
                    checked={parallel}
                    disabled={engines.length < 2}
                    onChange={() => toggleParallel(f.key)}
                  />
                  Chạy song song
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button
          className="aim__btn aim__btn--primary"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? 'Đang lưu…' : dirty ? 'Lưu thay đổi' : 'Đã lưu'}
        </button>
      </div>
    </div>
  );
};

// ─── Page shell ───────────────────────────────────────────────────────────

const AiAdminPage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminRecord[]>([]);
  const [registry, setRegistry] = useState<Registry | null>(null);
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [tab, setTab] = useState<'keys' | 'matrix'>('keys');

  useEffect(() => {
    (async () => {
      const [a, r] = await Promise.all([fetchAdmins(), fetchRegistry()]);
      if (a.EC === 0) {
        setAdmins(a.DT);
        if (a.DT.length) setSelectedAdminId(a.DT[0].id);
      }
      if (r.EC === 0) setRegistry(r.DT);
    })();
  }, []);

  return (
    <div className="aim">
      <div className="aim__header">
        <div>
          <h1 className="aim__title">Quản lý AI</h1>
          <p className="aim__subtitle">
            Gán key AI cho từng đơn vị Admin và chọn engine chạy cho mỗi tính năng.
          </p>
        </div>
      </div>

      <div className="aim__toolbar">
        <label className="aim__label">Đơn vị:</label>
        <select
          className="aim__select"
          value={selectedAdminId ?? ''}
          onChange={(e) => setSelectedAdminId(Number(e.target.value) || null)}
        >
          {admins.map((a) => (
            <option key={a.id} value={a.id}>
              {a.username} · {a.email}
            </option>
          ))}
        </select>
      </div>

      <div className="aim__tabs">
        <button
          className={`aim__tab ${tab === 'keys' ? 'aim__tab--active' : ''}`}
          onClick={() => setTab('keys')}
        >
          API Keys
        </button>
        <button
          className={`aim__tab ${tab === 'matrix' ? 'aim__tab--active' : ''}`}
          onClick={() => setTab('matrix')}
        >
          Ma trận tính năng
        </button>
      </div>

      <div className="aim__panel">
        {!selectedAdminId || !registry ? (
          <div className="aim__empty">Chọn đơn vị để bắt đầu</div>
        ) : tab === 'keys' ? (
          <KeysPanel adminId={selectedAdminId} registry={registry} />
        ) : (
          <MatrixPanel adminId={selectedAdminId} registry={registry} />
        )}
      </div>
    </div>
  );
};

export default AiAdminPage;
